import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Truck, RotateCcw, Store, MessageCircle } from "lucide-react";
import Stars from "@/components/Stars";
import ProductReviews from "@/components/ProductReviews";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, setCartCount } = useAuth();
  const [product, setProduct] = useState(null);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
      } catch {
        toast.error("Product not found");
      }
    })();
  }, [id]);

  const addToCart = async () => {
    if (!user) {
      nav("/login");
      return;
    }
    try {
      const { data } = await api.post("/cart", { product_id: id, qty });
      setCartCount(data.count);
      toast.success("Added to cart");
    } catch (e) {
      toast.error("Could not add to cart");
    }
  };

  const buyNow = async () => {
    if (!user) { nav("/login"); return; }
    await addToCart();
    nav("/checkout");
  };

  if (!product) return <div className="p-10 text-center text-[#3B5249]">Loading…</div>;

  const off = product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  const images = product.images?.length ? product.images : ["https://images.unsplash.com/photo-1561715276-a2d087060f1d"];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="rounded-2xl overflow-hidden border border-[#E2E8E5] bg-white aspect-square" data-testid="product-main-image">
            <img src={images[img]} alt={product.title} className="w-full h-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-auto">
              {images.map((im, i) => (
                <button key={i} onClick={() => setImg(i)} className={`w-20 h-20 rounded-lg overflow-hidden border ${i===img?"border-[#14A085]":"border-[#E2E8E5]"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">{product.category}</div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0F3D2E] mt-1" data-testid="product-title">{product.title}</h1>
          {(product.review_count > 0 || product.avg_rating > 0) && (
            <div className="mt-2 flex items-center gap-2" data-testid="product-rating-summary">
              <Stars value={product.avg_rating || 0} size={16} />
              <span className="text-sm text-[#0F3D2E] font-medium">{(product.avg_rating || 0).toFixed(1)}</span>
              <span className="text-sm text-[#6E877C]">({product.review_count} review{product.review_count === 1 ? "" : "s"})</span>
            </div>
          )}
          <div className="mt-4 flex items-baseline gap-3">
            <span className="text-3xl font-semibold text-[#0F3D2E]" data-testid="product-price">₹{product.price}</span>
            {off > 0 && (
              <>
                <span className="text-lg text-[#6E877C] line-through">₹{product.mrp}</span>
                <span className="text-sm text-[#14A085] font-medium">{off}% off</span>
              </>
            )}
          </div>
          <p className="mt-6 text-[#3B5249] leading-relaxed" data-testid="product-description">{product.description}</p>

          {product.vendor_name && (
            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-[#0F3D2E]">
              <div className="flex items-center gap-2">
                <Store size={16} className="text-[#14A085]" />
                Sold by <span className="font-medium">{product.vendor_name}</span>
                {product.vendor_city && (
                  <span className="text-xs text-[#6E877C]">· {product.vendor_city}, {product.vendor_state}</span>
                )}
              </div>
              {product.vendor_phone && (
                <a
                  href={`https://wa.me/91${String(product.vendor_phone).replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Hi, I have a question about "${product.title}" on Florvanta.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white transition-colors"
                  data-testid="vendor-whatsapp-btn"
                >
                  <MessageCircle size={14} /> Chat with seller
                </a>
              )}
            </div>
          )}

          <div className="mt-6 flex items-center gap-3">
            <label className="text-sm text-[#3B5249]">Qty</label>
            <div className="flex items-center border border-[#E2E8E5] rounded-full">
              <button onClick={() => setQty(Math.max(1, qty-1))} className="w-9 h-9" data-testid="qty-minus">-</button>
              <span className="px-3 min-w-[2rem] text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(product.stock || 99, qty+1))} className="w-9 h-9" data-testid="qty-plus">+</button>
            </div>
            <span className="text-xs text-[#6E877C]">{product.stock} in stock</span>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={addToCart} className="rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white px-8" data-testid="add-to-cart-btn">
              Add to Cart
            </Button>
            <Button onClick={buyNow} variant="outline" className="rounded-full border-[#0F3D2E] text-[#0F3D2E] hover:bg-[#E8F5F2] px-8" data-testid="buy-now-btn">
              Buy Now
            </Button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3 text-xs">
            {[
              [Truck, "PAN-India Shipping"],
              [ShieldCheck, "Verified Vendor"],
              [RotateCcw, "Easy Returns"],
            ].map(([Icon, t], i) => (
              <div key={i} className="rounded-lg border border-[#E2E8E5] bg-white p-3 flex items-center gap-2">
                <Icon size={16} className="text-[#14A085]" />
                <span className="text-[#3B5249]">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ProductReviews
        productId={product.id}
        avgRating={product.avg_rating || 0}
        reviewCount={product.review_count || 0}
        onChange={() => api.get(`/products/${product.id}`).then(({ data }) => setProduct(data))}
      />
    </div>
  );
}
